import Component from 'flarum/common/Component';

const ELEMENT_COLORS = {
  FIRE: { bg: '#fce4ec', color: '#c62828' },
  WATER: { bg: '#e3f2fd', color: '#1565c0' },
  AIR: { bg: '#e8f5e9', color: '#2e7d32' },
  EARTH: { bg: '#fff8e1', color: '#f57f17' },
};

/**
 * Markdown 渲染器。
 *
 * 优先使用 Flarum 内置的 s9e.TextFormatter.preview 渲染，
 * 同时补充扩展语法（表格、S2 element-grid、S2 flow、颜色语义）的自定义渲染。
 *
 * 用法：
 *   <Markdown text="**hello** world" />
 */
export default class Markdown extends Component {
  oncreate(vnode) {
    this.renderTo(vnode.dom);
  }

  onupdate(vnode) {
    this.renderTo(vnode.dom);
  }

  renderTo(dom) {
    const text = this.attrs.text || '';

    // 1. 提取 element-grid 块（必须在表格之前，避免表格正则误匹配）
    const elementGridBlocks = [];
    let processed = text.replace(/```element-grid\n([\s\S]*?)```/g, (match, content) => {
      const idx = elementGridBlocks.length;
      elementGridBlocks.push(content.trim());
      return `\n\u200bEGRID${idx}\u200b\n`;
    });

    // 2. 提取 flow 块
    const flowBlocks = [];
    processed = processed.replace(/```flow\n([\s\S]*?)```/g, (match, content) => {
      const idx = flowBlocks.length;
      flowBlocks.push(content.trim());
      return `\n\u200bFLOW${idx}\u200b\n`;
    });

    // 3. 提取表格块（s9e 不支持表格，单独处理）
    const tableBlocks = [];
    processed = processed.replace(/\n?\|.+\|[\s\S]*?(?=\n\n|\n$|$)/g, (match) => {
      const lines = match.trim().split('\n');
      if (lines.length >= 2 && /^\|[\s:-]+\|/.test(lines[1])) {
        const idx = tableBlocks.length;
        tableBlocks.push(lines);
        return `\n\u200bTABLE${idx}\u200b\n`;
      }
      return match;
    });

    // 4. 用 s9e 渲染其他 Markdown
    if (typeof s9e !== 'undefined' && s9e.TextFormatter && typeof s9e.TextFormatter.preview === 'function') {
      s9e.TextFormatter.preview(processed, dom);
    } else {
      dom.textContent = text;
      return;
    }

    // 5. 替换表格占位符
    let html = dom.innerHTML;
    tableBlocks.forEach((lines, idx) => {
      const tableHtml = this._renderTable(lines);
      html = html.split(`\u200bTABLE${idx}\u200b`).join(tableHtml);
    });

    // 6. 替换 element-grid 占位符
    elementGridBlocks.forEach((content, idx) => {
      const gridHtml = this._renderElementGrid(content);
      html = html.split(`\u200bEGRID${idx}\u200b`).join(gridHtml);
    });

    // 7. 替换 flow 占位符
    flowBlocks.forEach((content, idx) => {
      const flowHtml = this._renderFlow(content);
      html = html.split(`\u200bFLOW${idx}\u200b`).join(flowHtml);
    });

    // 8. 颜色语义替换（:good: / :bad: / :status:）— 全文范围
    html = html.replace(/:good:(.+?)(?=<|$)/g, '<span class="s2-good">$1</span>');
    html = html.replace(/:bad:(.+?)(?=<|$)/g, '<span class="s2-bad">$1</span>');
    html = html.replace(/:status:(.+?)(?=<|$)/g, '<span class="s2-status">$1</span>');

    dom.innerHTML = html;
  }

  /** 将 Markdown 表格块转为 HTML */
  _renderTable(lines) {
    if (lines.length < 2) return lines.join('<br>');

    const headers = this._parseTableRow(lines[0]);
    const bodyRows = lines.slice(2).map((line) => this._parseTableRow(line));

    let html = '<div class="TaroMd-table-wrap" style="overflow-x:auto;margin:8px 0;"><table style="border-collapse:collapse;width:100%;font-size:14px;">';

    if (headers.length > 0) {
      html += '<thead><tr>';
      headers.forEach((h) => {
        html += `<th style="border:1px solid #d2d6dc;padding:8px 12px;text-align:left;background:#f5f7fa;font-weight:600;">${this._escapeHtml(h.trim())}</th>`;
      });
      html += '</tr></thead>';
    }

    if (bodyRows.length > 0) {
      html += '<tbody>';
      bodyRows.forEach((row, ri) => {
        html += `<tr style="${ri % 2 === 0 ? '' : 'background:#fafbfc;'}">`;
        row.forEach((cell) => {
          html += `<td style="border:1px solid #d2d6dc;padding:8px 12px;text-align:left;">${this._escapeHtml(cell.trim())}</td>`;
        });
        html += '</tr>';
      });
      html += '</tbody>';
    }

    html += '</table></div>';
    return html;
  }

  /** 解析表格行的单元格 */
  _parseTableRow(line) {
    const trimmed = line.replace(/^\||\|$/g, '');
    return trimmed.split('|');
  }

  /** 渲染四元素静态统筹 3×3 网格 */
  _renderElementGrid(content) {
    try {
      const lines = content.split('\n').map((l) => l.trim()).filter((l) => l !== '');
      if (lines.length === 0) {
        return `<pre>${this._escapeHtml(content)}</pre>`;
      }
      const rows = lines.map((line) => line.split(','));

      let html = '<div class="s2-element-grid"><table class="s2-eg-table">';
      rows.forEach((row) => {
        html += '<tr>';
        row.forEach((cell) => {
          const trimmed = (cell || '').trim();
          if (trimmed === '') {
            html += '<td class="s2-eg-empty"></td>';
          } else if (/STATIC|统筹|COORDINATION/i.test(trimmed)) {
            html += `<td class="s2-eg-center">${this._escapeHtml(trimmed)}</td>`;
          } else {
            const elMatch = trimmed.match(/^(FIRE|WATER|AIR|EARTH)/i);
            if (elMatch) {
              const c = ELEMENT_COLORS[elMatch[1].toUpperCase()] || { bg: '#f0f4f8', color: '#333' };
              html += `<td class="s2-eg-element" style="background:${c.bg};color:${c.color};">${this._escapeHtml(trimmed)}</td>`;
            } else {
              html += `<td class="s2-eg-attribute">${this._escapeHtml(trimmed)}</td>`;
            }
          }
        });
        html += '</tr>';
      });
      html += '</table></div>';
      return html;
    } catch (e) {
      return `<pre>${this._escapeHtml(content)}</pre>`;
    }
  }

  /** 渲染四元素牌阵-元素流转流程图 */
  _renderFlow(content) {
    try {
      // 把"换行+空白+|"合并为"|"（处理多行截止说明）
      const merged = content.replace(/\n\s*\|/g, '|');
      const lines = merged.split('\n').map((l) => l.trim()).filter((l) => l !== '');

      let html = '<div class="s2-flow">';
      lines.forEach((line) => {
        const parts = line.split(/\s*-->\s*/).filter((p) => p.trim() !== '');
        if (parts.length === 0) return;

        html += '<div class="s2-flow-row">';
        parts.forEach((part, idx) => {
          // 提取截止说明：节点末尾的 |xxx
          let terminus = '';
          let nodeText = part;
          const pipeIdx = part.indexOf('|');
          if (pipeIdx >= 0) {
            nodeText = part.slice(0, pipeIdx).trim();
            terminus = part.slice(pipeIdx + 1).trim();
          }

          // 解析节点：阵位名 牌名 元素名
          const m = nodeText.match(/^(.+?)\s+(\S+)\s+(\S+元素)$/);
          if (m) {
            html += '<div class="s2-flow-node">';
            html += `<div class="s2-flow-position">${this._escapeHtml(m[1].trim())}</div>`;
            html += `<div class="s2-flow-card">${this._escapeHtml(m[2].trim())}</div>`;
            html += `<div class="s2-flow-element">${this._escapeHtml(m[3].trim())}</div>`;
            html += '</div>';
          } else {
            // 无法解析，原样显示
            html += `<div class="s2-flow-node">${this._escapeHtml(nodeText)}</div>`;
          }

          // 非末节点：加箭头
          if (idx < parts.length - 1) {
            html += '<div class="s2-flow-arrow">流转</div>';
          } else if (terminus) {
            // 末节点带截止说明
            html += '<div class="s2-flow-terminus">';
            html += `<span class="s2-flow-terminus-text">${this._escapeHtml(terminus)}</span>`;
            html += '</div>';
          }
        });
        html += '</div>';
      });
      html += '</div>';
      return html;
    } catch (e) {
      return `<pre>${this._escapeHtml(content)}</pre>`;
    }
  }

  _escapeHtml(text) {
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' };
    return String(text).replace(/[&<>"]/g, (c) => map[c]);
  }

  view() {
    return <div className="TaroMarkdown" />;
  }
}